import { apiClient } from '@/api/client';
import { authClient } from '@/config/auth-client';
import { createLogger } from '@/utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import React, { createContext, useContext, useEffect, useRef } from 'react';

const logger = createLogger('USE_AUTH');

// --- Types ---

export interface AuthUser {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  image?: string | null;
  username?: string;
  displayUsername?: string;
  role?: string;
  banned?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface AuthResult<T = unknown> {
  data?: T;
  error?: { message?: string; status?: number; code?: string; [key: string]: unknown };
}

type AuthContextType = {
  isLoggedIn: boolean;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (email: string, password: string, name: string, username: string, inviteCode?: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  sendVerificationOTP: (email: string) => Promise<AuthResult>;
  verifyEmailOTP: (email: string, otp: string) => Promise<AuthResult>;
  checkUsernameAvailability: (username: string) => Promise<AuthResult<{ available: boolean }>>;
  forgetPassword: (email: string) => Promise<AuthResult>;
  resetPassword: (token: string, newPassword: string) => Promise<AuthResult>;
  isLoading: boolean;
  error: Error | null;
};

// --- Helpers ---

const getPasswordResetRedirectUrl = () => {
  if (Platform.OS === 'web') {
    return `${typeof window !== 'undefined' ? window.location.origin : ''}/(auth)/reset-password`;
  }
  const schemeValue = Constants.expoConfig?.scheme;
  const scheme = Array.isArray(schemeValue) ? schemeValue[0] : (schemeValue || 'flavrly-dev');
  return `${scheme}://reset-password`;
};

// --- Context ---

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: session, isPending, isRefetching, error } = authClient.useSession();
  const queryClient = useQueryClient();

  // Preserve previous valid session to prevent brief loggedOut flash during re-validation
  // (e.g. when returning to a browser tab after a few minutes)
  const prevSessionRef = useRef(session);
  if (session) {
    prevSessionRef.current = session;
  } else if (!isPending && !isRefetching) {
    // Definitively logged out (not mid-refetch) -- clear the cache
    prevSessionRef.current = null;
  }

  // Use current session if available; during re-validation, fall back to previous
  const isFetchingSession = isPending || isRefetching;
  const effectiveSession = session ?? (isFetchingSession ? prevSessionRef.current : null);

  const isLoggedIn = !!effectiveSession?.user;
  const user = (effectiveSession?.user as AuthUser) || null;
  const isLoading = isPending && !prevSessionRef.current; // Only true on initial boot (no prior session)

  // Invalidate profile cache when the user changes (login/logout/switch)
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (prevUserIdRef.current !== currentUserId) {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id, queryClient]);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Login error:', error);
      return { error: { message: error.message || 'Login failed' } };
    }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    username: string,
    inviteCode?: string
  ): Promise<AuthResult> => {
    try {
      const signUpData: { email: string; password: string; name: string; username: string; inviteCode?: string } = {
        email,
        password,
        name,
        username,
      };
      if (inviteCode) {
        signUpData.inviteCode = inviteCode;
      }

      const result = await authClient.signUp.email(signUpData);
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Registration error:', error);
      return { error: { message: error.message || 'Registration failed' } };
    }
  };

  const logout = async () => {
    try {
      queryClient.clear();
      apiClient.clearTokenCache();
      await authClient.signOut();
    } catch (error) {
      logger.error('Logout error:', error);
      throw error;
    }
  };

  const sendVerificationOTP = async (email: string): Promise<AuthResult> => {
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'email-verification',
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Send OTP error:', error);
      return { error: { message: error.message || 'Failed to send verification OTP' } };
    }
  };

  const verifyEmailOTP = async (email: string, otp: string): Promise<AuthResult> => {
    try {
      const result = await authClient.emailOtp.verifyEmail({ email, otp });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Email OTP verification error:', error);
      return { error: { message: error.message || 'Email verification failed' } };
    }
  };

  const checkUsernameAvailability = async (username: string): Promise<AuthResult<{ available: boolean }>> => {
    try {
      const result = await (authClient as any).isUsernameAvailable({
        username: username.trim(),
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: { available: result.data?.available || false } };
    } catch (error: any) {
      logger.error('Username availability check error:', error);
      return { error: { message: error.message || 'Username check failed' } };
    }
  };

  const forgetPassword = async (email: string): Promise<AuthResult> => {
    try {
      const redirectTo = getPasswordResetRedirectUrl();
      const result = await (authClient as any).requestPasswordReset({
        email: email.trim(),
        redirectTo,
      });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Forget password error:', error);
      return { error: { message: error.message || 'Password reset request failed' } };
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<AuthResult> => {
    try {
      const result = await (authClient as any).resetPassword({ token, newPassword });
      if (result.error) {
        return { error: result.error };
      }
      return { data: result.data };
    } catch (error: any) {
      logger.error('Reset password error:', error);
      return { error: { message: error.message || 'Password reset failed' } };
    }
  };

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      user,
      login,
      register,
      logout,
      sendVerificationOTP,
      verifyEmailOTP,
      checkUsernameAvailability,
      forgetPassword,
      resetPassword,
      isLoading,
      error,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
